import React, { useState } from 'react';
import { ChevronDown, Cloud, Triangle, Mail, Mailbox, MessageSquare, Calendar } from 'lucide-react';
import { users, connectors, crmStages, signalConfig } from '../data/mvpMockData';
import Walkthrough from '../components/Walkthrough';

const tabs = ['Users', 'CRM Stages', 'Signals', 'Connectors'];

const walkthroughSteps = [
  { target: '[data-testid="dashboard-header"]', title: 'Settings', description: 'CRO / Admin view, configure Forsyt to match your org. Manage users, map CRM stages, set risk signal thresholds, and connect your tools (Salesforce, Gong, Slack, and more).' },
  { target: '[data-testid="tab-users"]', title: 'Users', description: 'Manage your team members. View everyone\'s name, email, role, and status. Add, edit, or remove users from this tab.' },
  { target: '[data-testid="invite-user-btn"]', title: 'Invite Team Members', description: 'Add new team members by email and assign them a role (Admin, AE, or CSM). They\'ll receive access based on their permissions.' },
  { target: '[data-testid="tab-crm-stages"]', title: 'CRM Stages', description: 'Map your pipeline stages to Forsyt categories. Toggle which stages are active and define their order. This controls how deals flow through your pipeline.' },
  { target: '[data-testid="tab-signals"]', title: 'Signals', description: 'Set risk thresholds that trigger alerts. Configure inactivity days before flagging a deal, and stage SLA limits for each pipeline phase.' },
  { target: '[data-testid="tab-connectors"]', title: 'Connectors', description: 'Integrate with your existing tools, Salesforce, HubSpot, Slack, Gmail, Gong, and more. Connect or disconnect services with one click.' },
];

const roleColors = {
  'Admin': 'bg-gray-100 text-gray-700',
  'AE': 'bg-blue-100 text-blue-700',
  'CSM': 'bg-green-100 text-green-700',
};

const connectorIcons = {
  cloud: Cloud,
  triangle: Triangle,
  mail: Mail,
  mailbox: Mailbox,
  message: MessageSquare,
  calendar: Calendar,
};

const statusColors = {
  'Connected': 'text-green-600',
  'Not Connected': 'text-gray-500',
  'Placeholder': 'text-yellow-600',
};

const statusBgColors = {
  'Connected': 'bg-green-50',
  'Not Connected': 'bg-gray-50',
  'Placeholder': 'bg-yellow-50',
};

export default function Settings() {
  const [activeTab, setActiveTab] = useState('Users');
  const [stageValues, setStageValues] = useState(
    crmStages.reduce((acc, s) => ({ ...acc, [s.name]: s.mapping }), {})
  );
  const [inactivityDays, setInactivityDays] = useState(signalConfig.inactivityThreshold);
  const [slaValues, setSlaValues] = useState({ ...signalConfig.stageSLA });

  return (
    <div className="space-y-6" data-testid="settings-page">
      <Walkthrough steps={walkthroughSteps} pageKey="settings" />
      {/* Tabs */}
      <div className="border-b border-gray-200" data-testid="settings-tabs">
        <nav className="flex gap-8">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`pb-3 text-sm font-medium transition-colors relative ${
                activeTab === tab
                  ? 'text-gray-900 after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:bg-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid={`tab-${tab.toLowerCase().replace(' ', '-')}`}
            >
              {tab}
            </button>
          ))}
        </nav>
      </div>

      {/* ===== Users Tab ===== */}
      {activeTab === 'Users' && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm" data-testid="users-card">
          <div className="px-6 py-5 border-b border-gray-100">
            <h2 className="text-lg font-bold text-gray-900">Team Members</h2>
            <p className="text-sm text-gray-500 mt-1">Manage users and their access to the platform.</p>
          </div>
          <div className="px-6 py-5">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-base font-semibold text-gray-900">Users</h3>
              <button className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-full transition-colors" data-testid="invite-user-btn">
                Invite User
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left text-xs font-medium text-gray-500 pb-3 w-[22%]">User</th>
                    <th className="text-left text-xs font-medium text-gray-500 pb-3">Email</th>
                    <th className="text-left text-xs font-medium text-gray-500 pb-3">Role</th>
                    <th className="text-left text-xs font-medium text-gray-500 pb-3">Status</th>
                    <th className="text-left text-xs font-medium text-gray-500 pb-3">Last Active</th>
                    <th className="text-left text-xs font-medium text-gray-500 pb-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} className="border-b border-gray-50" data-testid={`user-row-${user.id}`}>
                      <td className="py-5">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full ${user.avatarColor} flex items-center justify-center text-white text-sm font-bold flex-shrink-0`}>
                            {user.avatar}
                          </div>
                          <span className="text-sm font-medium text-gray-900">{user.name}</span>
                        </div>
                      </td>
                      <td className="py-5"><span className="text-sm text-gray-600">{user.email}</span></td>
                      <td className="py-5">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${roleColors[user.role] || 'bg-gray-100 text-gray-700'}`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="py-5">
                        <div className="flex items-center gap-1.5">
                          <span className={`w-2 h-2 rounded-full ${user.status === 'Connected' ? 'bg-green-500' : 'bg-gray-400'}`} />
                          <span className={`text-sm font-medium ${user.status === 'Connected' ? 'text-green-600' : 'text-gray-500'}`}>{user.status}</span>
                        </div>
                      </td>
                      <td className="py-5"><span className="text-sm text-gray-600">{user.lastActive}</span></td>
                      <td className="py-5">
                        <button className="text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors" data-testid={`edit-user-${user.id}`}>Edit</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ===== CRM Stages Tab ===== */}
      {activeTab === 'CRM Stages' && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm" data-testid="crm-stages-card">
          <div className="px-6 py-5 border-b border-gray-100">
            <h2 className="text-lg font-bold text-gray-900">CRM Stage Mapping</h2>
            <p className="text-sm text-gray-500 mt-1">Map your CRM stages to our standard pipeline stages.</p>
          </div>
          <div className="px-6 py-6 space-y-0">
            {crmStages.map((stage, i) => (
              <div key={stage.name} className={`flex items-center justify-between py-6 ${i < crmStages.length - 1 ? 'border-b border-gray-100' : ''}`} data-testid={`crm-stage-${stage.name.toLowerCase()}`}>
                <div>
                  <h3 className="text-sm font-bold text-gray-900">{stage.name}</h3>
                  <p className="text-xs text-gray-500 mt-0.5">{stage.description}</p>
                </div>
                <div className="relative w-[260px]">
                  <select
                    value={stageValues[stage.name]}
                    onChange={(e) => setStageValues(prev => ({ ...prev, [stage.name]: e.target.value }))}
                    className="w-full appearance-none px-4 py-3 pr-10 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700"
                  >
                    {['Discovery', 'Qualification', 'Proposal', 'Negotiation', 'Closed Won', 'Closed Lost'].map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              </div>
            ))}
            <div className="flex items-center gap-3 pt-6">
              <button className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors" data-testid="save-crm-stages">
                Save Changes
              </button>
              <button className="px-6 py-2.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium rounded-lg transition-colors" data-testid="reset-crm-stages">
                Reset
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Signals Tab ===== */}
      {activeTab === 'Signals' && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm" data-testid="signals-card">
          <div className="px-6 py-5 border-b border-gray-100">
            <h2 className="text-lg font-bold text-gray-900">Signal Configuration</h2>
            <p className="text-sm text-gray-500 mt-1">These defaults work for most teams. Adjust if your sales cycle is longer or shorter.</p>
          </div>
          <div className="px-6 py-6 space-y-8">
            {/* Inactivity Threshold */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">General Inactivity Threshold</label>
              <div className="relative w-full max-w-md">
                <input
                  type="number"
                  value={inactivityDays}
                  onChange={(e) => setInactivityDays(Number(e.target.value))}
                  className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 pr-14"
                  data-testid="inactivity-threshold"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-gray-500">days</span>
              </div>
              <p className="text-xs text-gray-500">Flags deals where no activity (emails, calls, meetings) has occurred within this period.</p>
            </div>

            <div className="border-t border-gray-100" />

            {/* Stage SLA Durations */}
            <div className="space-y-3">
              <h3 className="text-base font-bold text-gray-900">Stage SLA Durations</h3>
              <p className="text-xs text-gray-500 mb-4">Maximum days a deal should stay in each stage before being flagged as 'Stalled'.</p>
              <div className="grid grid-cols-2 gap-x-6 gap-y-5">
                {Object.entries(slaValues).map(([stage, days]) => (
                  <div key={stage} className="space-y-1.5" data-testid={`sla-${stage.toLowerCase()}`}>
                    <label className="text-sm font-medium text-gray-700">{stage}</label>
                    <div className="relative">
                      <input
                        type="number"
                        value={days}
                        onChange={(e) => setSlaValues(prev => ({ ...prev, [stage]: Number(e.target.value) }))}
                        className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 pr-10"
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-gray-500">d</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== Connectors Tab ===== */}
      {activeTab === 'Connectors' && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm" data-testid="connectors-card">
          <div className="px-6 py-5 border-b border-gray-100">
            <h2 className="text-lg font-bold text-gray-900">Connectors</h2>
            <p className="text-sm text-gray-500 mt-1">Connect your tools to power signals and revenue intelligence.</p>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-3 gap-5">
              {connectors.map((connector) => {
                const IconComp = connectorIcons[connector.icon] || Cloud;
                const isConnected = connector.status === 'Connected';
                return (
                  <div key={connector.id} className="border border-gray-200 rounded-xl p-5 flex flex-col justify-between min-h-[200px]" data-testid={`connector-${connector.name.toLowerCase()}`}>
                    <div>
                      <div className="flex items-start justify-between mb-4">
                        <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                          <IconComp className="w-5 h-5 text-gray-500" />
                        </div>
                        <span className={`text-xs font-semibold ${statusColors[connector.status]}`}>
                          {connector.status}
                        </span>
                      </div>
                      <h3 className="text-base font-bold text-gray-900 mb-1">{connector.name}</h3>
                      <p className="text-sm text-gray-500">{connector.description}</p>
                    </div>
                    <button
                      className={`w-full mt-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                        isConnected
                          ? 'bg-white border border-gray-300 hover:bg-gray-50 text-gray-700'
                          : 'bg-blue-600 hover:bg-blue-700 text-white'
                      }`}
                      data-testid={`connector-btn-${connector.name.toLowerCase()}`}
                    >
                      {isConnected ? 'Manage' : 'Connect'}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
